/**
 * Auto-Pilot fire detail (Screen 4) — timeline of every step in the fire plus
 * the grouped outputs tree (briefs with nested off-page variants, teardowns,
 * PDFs). Auto fires are `campaigns` rows, so the manual-campaign hooks work
 * unchanged; this page just presents them fire-first instead of goal-first.
 */
import { useParams } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { Bot } from 'lucide-react';
import { PageHeader } from '@/components/page-header';
import { StatusPill } from '@/components/status-pill';
import { DetailError } from '@/components/detail-page';
import { ErrorBanner } from '@/components/error-banner';
import { fadeUp, stagger } from '@/components/motion';
import { FireTimeline } from '@/components/auto-campaign/fire-timeline';
import { OutputsTree } from '@/components/auto-campaign/outputs-tree';
import { useCampaign } from '@/features/campaigns/use-campaign';
import { useCampaignSteps } from '@/features/campaigns/use-campaign-steps';

export function AutoFireDetailRoute(): JSX.Element {
  const { fireId } = useParams({ strict: false }) as { fireId: string };
  const campaign = useCampaign(fireId);
  const steps = useCampaignSteps(fireId);

  const c = campaign.data;

  if (campaign.error) {
    return (
      <div className="space-y-4">
        <DetailError
          error={campaign.error}
          entityKind="Auto-Pilot run"
          backTo="/campaigns"
          backLabel="Campaigns"
        />
      </div>
    );
  }

  return (
    <motion.div className="space-y-6" initial="hidden" animate="show" variants={stagger}>
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Auto-Pilot run"
          eyebrow={
            <span className="flex items-center gap-1.5">
              <Bot className="h-3.5 w-3.5" />
              Auto-Pilot
            </span>
          }
          actions={c ? <StatusPill status={c.status} /> : null}
          description={
            c
              ? `Started ${new Date(c.created_at).toLocaleString()} — every step this run executed, with its outputs below.`
              : '...'
          }
        />
      </motion.div>

      {steps.error ? <ErrorBanner error={steps.error} /> : null}

      <motion.div variants={fadeUp}>
        <FireTimeline
          campaignId={fireId}
          steps={steps.data?.items}
          isLoading={steps.isLoading}
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <OutputsTree campaign={c} steps={steps.data?.items} />
      </motion.div>
    </motion.div>
  );
}
